import { defineComponent, h, ref, toRef } from 'vue';

import { useFlattenRecords } from '../../composable';
import { useInjectBody, useInjectResize, useInjectTable, useProvideHover } from '../../context';
import { getColumnsKey } from '../../utils';
import { MeasureCell } from '../Cell';

import BodyRow from './BodyRow';
import ExpandedRow from './ExpandedRow';

import type { NTable } from '../../interface';

interface IBodyProps<RecordType = NTable.TDefaultRecord> {
  data: RecordType[];
  getRowKey: NTable.NRow.TGetRowKey<RecordType>;
  measureColumnWidth: boolean;
  expandedKeys: Set<NTable.TKey>;
  customRow?: NTable.NCustomize.TGetComponentProps<RecordType>;
  rowExpandable: NTable.NExpand.IExpandable['rowExpandable'];
  childrenColumnName: string;
}

export default defineComponent<IBodyProps>({
  // eslint-disable-next-line vue/no-reserved-component-names
  name: 'Body',
  props: [
    'data',
    'getRowKey',
    'measureColumnWidth',
    'expandedKeys',
    'customRow',
    'rowExpandable',
    'childrenColumnName'
  ] as any,
  setup(props, { slots }) {
    const resizeContext = useInjectResize();
    const tableContext = useInjectTable();
    const bodyContext = useInjectBody();

    const flattenData = useFlattenRecords(
      toRef(props, 'data'),
      toRef(props, 'childrenColumnName'),
      toRef(props, 'expandedKeys'),
      toRef(props, 'getRowKey')
    );

    const startRow = ref(-1);
    const endRow = ref(-1);

    let timeoutId: any;

    useProvideHover({
      startRow,
      endRow,
      onHover: (start, end) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          startRow.value = start as number;
          endRow.value = end as number;
        }, 100);
      }
    });

    return () => {
      const { data, getRowKey, measureColumnWidth, expandedKeys, customRow, rowExpandable, childrenColumnName } = props;
      const { onColumnResize } = resizeContext;
      const { getComponent } = tableContext;
      const { flattenColumns } = bodyContext;

      const WrapperComponent = getComponent(['body', 'wrapper'], 'tbody');
      const trComponent = getComponent(['body', 'row'], 'tr');
      const tdComponent = getComponent(['body', 'cell'], 'td');

      let rows;

      if (data.length) {
        rows = flattenData.value.map((item, idx) => {
          const { record, indent, index: renderIndex } = item;
          const key = getRowKey(record, idx);

          return h(BodyRow, {
            key,
            record,
            renderIndex,
            recordKey: key,
            rowKey: key,
            index: idx,
            rowComponent: trComponent,
            cellComponent: tdComponent,
            expandedKeys,
            customRow,
            getRowKey,
            rowExpandable,
            childrenColumnName,
            indent
          });
        });
      } else {
        rows = h(
          ExpandedRow,
          {
            expanded: true,
            isEmpty: true,
            class: 'cds-table-placeholder',
            component: trComponent,
            cellComponent: tdComponent,
            colSpan: flattenColumns.length
          },
          {
            default: () => slots.emptyNode?.()
          }
        );
      }

      const columnsKey = getColumnsKey(flattenColumns);

      return h(WrapperComponent, { class: 'cds-table__tbody' }, [
        /* Measure body column width with additional hidden col */
        measureColumnWidth &&
          h(
            'tr',
            { ariaHidden: true, class: 'cds-table__measure-row', style: { height: 0, fontSize: 0 } },
            columnsKey.map((key) => h(MeasureCell, { key, columnKey: key, onColumnResize }))
          ),

        rows
      ]);
    };
  }
});
